import { useState } from "react";
import axios from "axios";
import { Instagram, Mail, ArrowUpRight, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { credits, imdbUrl } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AboutContact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", project_type: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Sent. I'll reply within 48 hours.", {
        description: "Thanks for reaching out.",
      });
      setForm({ name: "", email: "", message: "", project_type: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something broke. Email me directly.";
      toast.error(typeof detail === "string" ? detail : "Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="about"
      data-testid="section-about"
      className="relative bg-[#050505] py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Section header */}
        <div className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-2">
            <p className="font-accent text-[10px] text-[#D4AF37]">Part 03</p>
          </div>
          <div className="col-span-12 md:col-span-10">
            <h2 className="font-serif-display text-5xl md:text-7xl tracking-tighter text-white leading-[1]">
              Who&rsquo;s shooting.
            </h2>
          </div>
        </div>

        {/* About split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Bio */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="font-accent text-[10px] text-zinc-500 mb-6">
              About &mdash; Leigh Elford
            </p>
            <h3 className="font-serif-display text-3xl md:text-5xl text-white leading-[1.05] tracking-tight">
              Leigh Elford.
            </h3>
            <div className="mt-8 space-y-5 text-zinc-400 font-light leading-relaxed max-w-2xl">
              <p>
                Leigh Elford is a seasoned cinematographer and director of
                photography based in Auckland, New Zealand, with over a decade of
                experience in the film industry. A graduate of Unitec&rsquo;s School
                of Performing and Screen Arts, Leigh has cultivated a diverse
                portfolio that spans short films, music videos, and television
                productions.
              </p>
              <p>
                His approach to cinematography is deeply rooted in storytelling.
                Leigh emphasises composing each frame to convey narrative,
                using lighting to achieve dynamic tonality, and employing movement
                to shape or alter perspective &mdash; letting each story flourish
                visually, and treating every narrative with respect.
              </p>
              <p>
                Notable works include serving as director of photography for the
                short films <em>Torn</em> (2020) and <em>Treasure</em> (2019). Leigh
                is drawn to projects that challenge and inspire, and to
                collaborators &mdash; producers and directors &mdash; with a strong
                focus on story and character.
              </p>
            </div>

            {/* CV & Rate Card downloads */}
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                data-testid="download-cv"
                href="https://customer-assets-agu9un31.emergentagent.net/job_video-showcase-354/artifacts/tueizjjg_DoPshowreel.pdf"
                target="_blank"
                rel="noreferrer"
                download="Leigh-Elford-DoP-Showreel.pdf"
                className="group inline-flex items-center gap-4 border border-white/15 hover:border-[#D4AF37] px-6 py-3 transition-colors"
              >
                <Download size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
                <span className="font-accent text-[10px] text-zinc-300 group-hover:text-white transition-colors">
                  DoP Showreel &middot; PDF
                </span>
                <span className="h-px w-6 bg-zinc-700 group-hover:w-10 group-hover:bg-[#D4AF37] transition-all" />
              </a>
              <a
                data-testid="download-rate-card"
                href="https://customer-assets-agu9un31.emergentagent.net/job_video-showcase-354/artifacts/qc85a1i3_Leigh_Elford_Rate_Card_2025.pdf"
                target="_blank"
                rel="noreferrer"
                download="Leigh-Elford-Rate-Card-2025.pdf"
                className="group inline-flex items-center gap-4 border border-white/15 hover:border-[#D4AF37] px-6 py-3 transition-colors"
              >
                <Download size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
                <span className="font-accent text-[10px] text-zinc-300 group-hover:text-white transition-colors">
                  Rate Card 2025 &middot; PDF
                </span>
                <span className="h-px w-6 bg-zinc-700 group-hover:w-10 group-hover:bg-[#D4AF37] transition-all" />
              </a>
            </div>

            {/* Facts */}
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/10 pt-10">
              <div>
                <p className="font-serif-display text-2xl md:text-3xl text-white leading-tight">
                  Auckland
                </p>
                <p className="mt-2 font-accent text-[9px] text-zinc-500">New Zealand · based</p>
              </div>
              <div>
                <p className="font-serif-display text-2xl md:text-3xl text-white leading-tight">
                  10+ yrs
                </p>
                <p className="mt-2 font-accent text-[9px] text-zinc-500">On set</p>
              </div>
              <div>
                <p className="font-serif-display text-2xl md:text-3xl text-white leading-tight">
                  Unitec
                </p>
                <p className="mt-2 font-accent text-[9px] text-zinc-500">Performing &amp; Screen Arts</p>
              </div>
              <div>
                <p className="font-serif-display text-2xl md:text-3xl text-white leading-tight">
                  Story-first
                </p>
                <p className="mt-2 font-accent text-[9px] text-zinc-500">Approach</p>
              </div>
            </div>

            {/* Recognition */}
            <div className="mt-14">
              <p className="font-accent text-[10px] text-zinc-500 mb-6">
                Recognition
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  {
                    fest: "Amsterdam Short Film Festival",
                    prize: "Award Winner",
                    year: "2021",
                  },
                  {
                    fest: "Toronto Fantasy / Sci-Fi Film Festival",
                    prize: "Winner · Cinematography",
                    year: "2018",
                  },
                  {
                    fest: "\u00C9CU \u2014 The European Independent Film Festival",
                    prize: "Winner · Best Cinematography",
                    year: "2015",
                  },
                ].map((a, i) => (
                  <div
                    key={a.year + i}
                    data-testid={`award-${i}`}
                    className="group relative bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/40 transition-colors px-4 py-6 flex items-center gap-4"
                  >
                    {/* Left laurel */}
                    <svg
                      viewBox="0 0 40 100"
                      className="w-6 h-16 text-[#D4AF37] shrink-0"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M35 5 C 15 20, 5 45, 15 70 C 20 85, 25 92, 32 96 L 32 92 C 26 88, 22 82, 19 74 C 14 55, 22 32, 37 12 Z" opacity="0.9" />
                      <ellipse cx="12" cy="30" rx="6" ry="3" transform="rotate(-30 12 30)" opacity="0.85" />
                      <ellipse cx="9" cy="42" rx="7" ry="3" transform="rotate(-15 9 42)" opacity="0.85" />
                      <ellipse cx="8" cy="55" rx="7" ry="3" transform="rotate(0 8 55)" opacity="0.85" />
                      <ellipse cx="10" cy="68" rx="6" ry="3" transform="rotate(15 10 68)" opacity="0.85" />
                      <ellipse cx="15" cy="80" rx="5" ry="2.5" transform="rotate(30 15 80)" opacity="0.85" />
                    </svg>

                    <div className="flex-1 text-center">
                      <p className="font-accent text-[9px] text-[#D4AF37] mb-1">
                        {a.prize}
                      </p>
                      <p className="font-serif-display text-base md:text-lg text-white leading-snug">
                        {a.fest}
                      </p>
                      <p className="font-accent text-[10px] text-zinc-500 mt-2">
                        {a.year}
                      </p>
                    </div>

                    {/* Right laurel (mirrored) */}
                    <svg
                      viewBox="0 0 40 100"
                      className="w-6 h-16 text-[#D4AF37] shrink-0 -scale-x-100"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M35 5 C 15 20, 5 45, 15 70 C 20 85, 25 92, 32 96 L 32 92 C 26 88, 22 82, 19 74 C 14 55, 22 32, 37 12 Z" opacity="0.9" />
                      <ellipse cx="12" cy="30" rx="6" ry="3" transform="rotate(-30 12 30)" opacity="0.85" />
                      <ellipse cx="9" cy="42" rx="7" ry="3" transform="rotate(-15 9 42)" opacity="0.85" />
                      <ellipse cx="8" cy="55" rx="7" ry="3" transform="rotate(0 8 55)" opacity="0.85" />
                      <ellipse cx="10" cy="68" rx="6" ry="3" transform="rotate(15 10 68)" opacity="0.85" />
                      <ellipse cx="15" cy="80" rx="5" ry="2.5" transform="rotate(30 15 80)" opacity="0.85" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected credits */}
            <div className="mt-14">
              <div className="flex items-end justify-between mb-6">
                <p className="font-accent text-[10px] text-zinc-500">
                  Selected Credits &middot; via IMDb
                </p>
                <a
                  data-testid="imdb-link"
                  href={imdbUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2 font-accent text-[10px] text-zinc-500 hover:text-[#D4AF37] transition-colors"
                >
                  <span>Full filmography</span>
                  <ExternalLink size={11} strokeWidth={1.5} className="opacity-70 group-hover:opacity-100" />
                </a>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 divide-y divide-white/5 border-t border-white/5">
                {credits.map((c, i) => (
                  <li
                    key={`${c.title}-${c.year}-${i}`}
                    className="group grid grid-cols-[3rem_1fr_auto] items-baseline gap-4 py-3"
                  >
                    <span className="font-accent text-[10px] text-zinc-600 group-hover:text-[#D4AF37] transition-colors">
                      {c.year}
                    </span>
                    <span className="text-zinc-200 font-light text-sm md:text-base leading-snug">
                      {c.title}
                      <span className="hidden md:inline text-zinc-500"> &middot; {c.role}</span>
                    </span>
                    <span className="font-accent text-[9px] text-zinc-600 whitespace-nowrap">
                      {c.format}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Portrait */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-32">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_video-showcase-354/artifacts/csb7risj_IMG_3445.webp"
                alt="Lee behind the ARRI"
                className="absolute inset-0 w-full h-full object-cover object-left transition-all duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-accent text-[9px] text-white/80">
                  On set · behind the ARRI
                </span>
                <span className="font-accent text-[9px] text-[#D4AF37]">B&amp;W</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-24 md:mt-36 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 border-t border-white/10 pt-16 md:pt-24">
          <div className="lg:col-span-5">
            <p className="font-accent text-[10px] text-[#D4AF37] mb-6">Get in touch</p>
            <h3 className="font-serif-display text-4xl md:text-6xl tracking-tighter text-white leading-[0.95]">
              Have a film in your head?
              <br />
              <em className="italic text-zinc-500">Let&rsquo;s shoot it.</em>
            </h3>

            <div className="mt-10 space-y-5">
              <a
                data-testid="contact-email-link"
                href="mailto:elfordleeman@gmail.com"
                className="group flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors"
              >
                <Mail size={18} strokeWidth={1} />
                <span className="font-light text-lg">elfordleeman@gmail.com</span>
                <ArrowUpRight size={16} strokeWidth={1} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </a>
              <a
                data-testid="social-link-instagram"
                href="https://www.instagram.com/leeman09/"
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 text-zinc-300 hover:text-[#D4AF37] transition-colors"
              >
                <Instagram size={18} strokeWidth={1} />
                <span className="font-light text-lg">@leeman09</span>
                <ArrowUpRight size={16} strokeWidth={1} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </a>
            </div>

            <p className="mt-12 font-accent text-[10px] text-zinc-500 max-w-xs">
              Currently booking projects for Q1 &amp; Q2 &mdash; Music video, brand,
              short film.
            </p>
          </div>

          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 space-y-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-accent text-[9px] text-zinc-500 block mb-3">
                  01 · Name
                </label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] outline-none px-0 py-3 text-white placeholder:text-zinc-700 font-light text-lg transition-colors"
                  required
                />
              </div>
              <div>
                <label className="font-accent text-[9px] text-zinc-500 block mb-3">
                  02 · Email
                </label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@studio.com"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] outline-none px-0 py-3 text-white placeholder:text-zinc-700 font-light text-lg transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label className="font-accent text-[9px] text-zinc-500 block mb-3">
                03 · Project type
              </label>
              <div className="flex flex-wrap gap-2">
                {["Music Video", "Brand Film", "Short Film", "Documentary", "Other"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`project-type-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, project_type: p }))}
                    className={`font-accent text-[10px] px-4 py-2 border transition-colors ${
                      form.project_type === p
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-accent text-[9px] text-zinc-500 block mb-3">
                04 · Tell me about it
              </label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={update("message")}
                placeholder="A song, a script, a feeling, a budget — whatever you have."
                rows={4}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] outline-none px-0 py-3 text-white placeholder:text-zinc-700 font-light text-lg transition-colors resize-none"
                required
              />
            </div>

            <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <p className="font-accent text-[9px] text-zinc-600 max-w-xs">
                Replies within 48h. Quotes after a 20-minute call.
              </p>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center gap-4 bg-white text-black hover:bg-[#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-10 py-4 font-accent text-[11px]"
              >
                <span>{submitting ? "Sending…" : "Send Message"}</span>
                <span className="h-px w-6 bg-black group-hover:w-12 transition-all" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
